import * as React from 'react';

import { generatePath, Link, matchPath, type Params, useLocation, useMatches } from 'react-router-dom';

import { BreadcrumbLabel } from '@app/navigation/BreadcrumbLabel';
import { routes } from '@app/routes/routes';
import { useAppSelector } from '@app/store/store';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@shared/components/ui';
import { OiWordmark } from '@shared/icons';
import forwardSlashIcon from '@shared/icons/svg/forward-slash.svg';
import pipeIcon from '@shared/icons/svg/pipe.svg';

import { urls } from '../urls';

import { buildBreadcrumbs, findRouteByPathTemplate } from './breadcrumb.utils';

interface CustomRouteHandle {
  crumb?: (params: Params<string>) => React.ReactNode;
}

export const AppBreadcrumb = () => {
  const location = useLocation();
  const matches = useMatches();
  const { hideInsightParentBreadcrumb } = useAppSelector((state) => state.investigations);

  const isTraceInsightRoute = Boolean(
    matchPath({ path: urls.traceFindingsInsight.path, end: true }, location.pathname),
  );

  const crumbs = buildBreadcrumbs(urls, location.pathname).filter(
    (crumb) => !(isTraceInsightRoute && hideInsightParentBreadcrumb && crumb.path === urls.traceFindings.path),
  );

  if (crumbs.length === 0) return null;

  const leafMatch = matches.at(-1);
  const runtimeParams = leafMatch?.params ?? {};

  return (
    <div className="flex items-center ml-6">
      <Link to={urls.results.path} className="cursor-pointer">
        <OiWordmark className="mt-px" />
      </Link>
      <img src={pipeIcon} className="size-6" alt="|" />

      <Breadcrumb>
        <BreadcrumbList>
          {crumbs.map((crumb, idx) => {
            const isLast = idx === crumbs.length - 1;
            const linkPath = generatePath(crumb.path, runtimeParams);
            const configRoute = findRouteByPathTemplate(crumb.path, routes);
            const handle = configRoute?.handle as CustomRouteHandle | undefined;
            const dynamicLabel = handle?.crumb ? handle.crumb(runtimeParams) : null;

            return (
              <React.Fragment key={crumb.path}>
                <BreadcrumbItem>
                  {isLast ? (
                    <BreadcrumbPage className="text-foreground leading-5">
                      {dynamicLabel ?? <BreadcrumbLabel label={crumb.label} />}
                    </BreadcrumbPage>
                  ) : (
                    <BreadcrumbLink asChild>
                      <Link
                        className="leading-5 text-primary hover:text-primary hover:[&_*]:underline underline-offset-5"
                        to={linkPath}
                      >
                        {dynamicLabel ?? <BreadcrumbLabel label={crumb.label} />}
                      </Link>
                    </BreadcrumbLink>
                  )}
                </BreadcrumbItem>

                {!isLast && (
                  <BreadcrumbSeparator>
                    <img src={forwardSlashIcon} className="h-6 pt-px" alt="/" />
                  </BreadcrumbSeparator>
                )}
              </React.Fragment>
            );
          })}
        </BreadcrumbList>
      </Breadcrumb>
    </div>
  );
};
